import type { Candle } from "../types";
import type { OverlayLine } from "./Chart";

/**
 * The chart's text alternative (GRA-148): one sentence of state, then a table
 * of the plotted candles. Visually hidden, always in the accessibility tree.
 *
 * A canvas has no accessible content at all, so without this the whole middle
 * of the screen was a named region holding nothing. The table is the same data
 * the candles draw, in the same order.
 *
 * Every number is a percent from the first plotted candle's open, never an
 * absolute price. That is not a shortcut: the chart hides its own price axis
 * until reveal (spec §0, "anonymization includes price"), and a screen-reader
 * table of raw OHLC would hand out exactly what the visible chart withholds.
 * The percent is also what the chart's own "from chart start" readout shows,
 * so the two say the same thing.
 */
export type ChartSummaryProps = {
  /** The active Loop, in words, e.g. "Predict · daily · trend" or "Trade". */
  modeLabel: string;
  /** The Context candles, as charted. */
  context: Candle[];
  /** The Forward candles, as charted (empty until reveal). */
  forward: Candle[];
  revealed: boolean;
  /** The price lines drawn over the candles: barriers, stop-loss, take-profit. */
  overlays: OverlayLine[];
};

/** Percent from `base`, with a sign, e.g. "+1.24%". */
function pct(value: number, base: number): string {
  if (base === 0) return "0.00%";
  const p = ((value - base) / base) * 100;
  return `${p >= 0 ? "+" : ""}${p.toFixed(2)}%`;
}

/**
 * The one-line state: which game, how much is on screen, and what the drawn
 * lines are. Overlay labels are already the anonymized text the chart prints
 * beside each line, so they can be read out as they are.
 */
export function summaryLine({
  modeLabel,
  context,
  forward,
  revealed,
  overlays,
}: ChartSummaryProps): string {
  const levels = overlays
    .map((o) => o.label)
    .filter((label): label is string => Boolean(label));
  const parts = [
    modeLabel,
    `${context.length} context ${context.length === 1 ? "candle" : "candles"}`,
    revealed
      ? `${forward.length} forward ${forward.length === 1 ? "candle" : "candles"} revealed`
      : "forward candles hidden until you commit",
  ];
  if (levels.length > 0) parts.push(`levels drawn: ${levels.join(", ")}`);
  return `${parts.join(". ")}.`;
}

export function ChartSummary(props: ChartSummaryProps) {
  const { context, forward, revealed } = props;
  const candles = revealed ? [...context, ...forward] : context;
  const base = candles[0]?.open ?? 0;

  return (
    <div className="sr-only">
      <p>{summaryLine(props)}</p>
      <table>
        <caption>
          Plotted candles, each price as a percent from the first candle&rsquo;s open
        </caption>
        <thead>
          <tr>
            <th scope="col">candle</th>
            <th scope="col">open</th>
            <th scope="col">high</th>
            <th scope="col">low</th>
            <th scope="col">close</th>
          </tr>
        </thead>
        <tbody>
          {candles.map((candle, index) => (
            <tr key={`${candle.time}-${index}`}>
              <th scope="row">
                {index + 1}
                {revealed && index >= context.length ? " (forward)" : ""}
              </th>
              <td>{pct(candle.open, base)}</td>
              <td>{pct(candle.high, base)}</td>
              <td>{pct(candle.low, base)}</td>
              <td>{pct(candle.close, base)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
